import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { RefreshCw, Heart } from "lucide-react";
import { pickCurated } from "../../data/curatedQuotes";
import { getFavorites, isFavorite, toggleFavorite, pickFavorite } from "@/lib/favoriteQuotes";
import { FavoriteQuotes } from "./FavoriteQuotes";
import { useIdle } from "@/lib/useIdle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const QuoteDisplay = () => {
  // `quote` is always an object: { text, author? }.
  const [quote, setQuote] = useState(() => pickCurated());
  const [source, setSource] = useState("curated");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(() => isFavorite(quote.text));
  const [favCount, setFavCount] = useState(() => getFavorites().length);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  // After 7s of stillness these controls soften to 45% so the background
  // and the line itself carry the moment.
  const idle = useIdle(7000);

  useEffect(() => {
    setSaved(isFavorite(quote.text));
  }, [quote.text]);

  const handleHeart = () => {
    const nowSaved = toggleFavorite(quote);
    setSaved(nowSaved);
    setFavCount(getFavorites().length);
  };

  const handleFavoritesClose = () => {
    setFavoritesOpen(false);
    // Refresh in case lines were let go inside the overlay.
    setFavCount(getFavorites().length);
    setSaved(isFavorite(quote.text));
  };

  const tapTimesRef = useRef([]);
  const quoteTextRef = useRef(quote.text);

  useEffect(() => {
    quoteTextRef.current = quote.text;
  }, [quote.text]);

  const loadQuote = useCallback(async () => {
    setLoading(true);

    // "Spiraling" signal: 3+ requests inside 30s. When someone is cycling
    // fast, a line that helped them before lands better than a fresh one.
    const now = Date.now();
    tapTimesRef.current = [...tapTimesRef.current, now].filter(
      (t) => now - t < 30000
    );
    const spiraling = tapTimesRef.current.length >= 3;

    const surfaceKept = (prevText) => {
      const kept = pickFavorite(prevText);
      if (!kept) return false;
      setQuote({ text: kept.text, author: kept.author });
      setSource("kept");
      setLoading(false);
      return true;
    };

    if (spiraling && surfaceKept(quoteTextRef.current)) {
      tapTimesRef.current = [];
      return;
    }

    // Try AI first when online; fall back to curated on any failure.
    if (navigator.onLine) {
      try {
        const res = await axios.get(`${API}/quotes/ai`, { timeout: 12000 });
        if (res?.data?.text) {
          setQuote({ text: res.data.text });
          setSource("ai");
          setLoading(false);
          return;
        }
      } catch (_) {
        // silent fallback
      }
    }
    // Offline / AI unavailable: gently alternate kept lines into the mix.
    if (Math.random() < 0.5 && surfaceKept(quoteTextRef.current)) return;
    setQuote((prev) => pickCurated(prev?.text));
    setSource("curated");
    setLoading(false);
  }, []);

  useEffect(() => {
    // Initial: give the user a quiet static curated quote instantly, then quietly
    // try to enrich with an AI one after a short delay so nothing jumps at them.
    const t = setTimeout(() => loadQuote(), 1200);
    return () => clearTimeout(t);
  }, [loadQuote]);

  return (
    <div
      className="relative z-10 flex flex-col items-center justify-center px-6 max-w-3xl mx-auto text-center"
      data-testid="quote-container"
    >
      <AnimatePresence mode="wait">
        <motion.blockquote
          key={quote.text}
          initial={{ opacity: 0, y: 12, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -8, filter: "blur(6px)" }}
          transition={{ duration: 1.6, ease: "easeInOut" }}
          className="font-heading italic text-white/90 leading-relaxed drop-shadow-md text-3xl sm:text-4xl lg:text-5xl"
          data-testid="quote-text"
        >
          {quote.text}
          {quote.author && (
            <motion.cite
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.4, ease: "easeInOut", delay: 0.6 }}
              className="font-hand not-italic block mt-5 text-2xl sm:text-3xl text-white/60 tracking-wide"
              style={{ fontFamily: '"Beth Ellen", "Caveat", cursive' }}
              data-testid="quote-author"
            >
              — {quote.author}
            </motion.cite>
          )}
        </motion.blockquote>
      </AnimatePresence>

      <div
        className={`flex flex-col items-center transition-opacity duration-[1500ms] ease-in-out ${
          idle ? "opacity-[0.45]" : "opacity-100"
        }`}
        data-testid="quote-controls-fader"
        data-idle={idle}
      >
      <div className="mt-10 flex items-center gap-3">
        <motion.button
          onClick={loadQuote}
          disabled={loading}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 backdrop-blur-xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 disabled:opacity-50 text-sm tracking-wide"
          data-testid="new-quote-btn"
          aria-label="Bring me another calming line"
        >
          <RefreshCw
            size={16}
            strokeWidth={1.5}
            className={loading ? "animate-spin" : ""}
          />
          <span>{loading ? "listening…" : "another line"}</span>
        </motion.button>

        <motion.button
          onClick={handleHeart}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className={`w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-xl border transition-colors duration-500 ${
            saved
              ? "bg-rose-300/15 border-rose-200/25 text-rose-200"
              : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white/85"
          }`}
          data-testid="favorite-quote-btn"
          aria-label={saved ? "Let this line go" : "Keep this line"}
          title={saved ? "kept" : "keep this line"}
        >
          <Heart
            size={16}
            strokeWidth={1.5}
            fill={saved ? "currentColor" : "none"}
          />
        </motion.button>
      </div>

      <p
        className="mt-3 text-xs text-white/40 tracking-wider uppercase"
        data-testid="quote-source"
      >
        {source === "ai"
          ? "written for this moment"
          : source === "kept"
          ? "a line you kept"
          : "a gentle reminder"}
      </p>

      {favCount > 0 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          onClick={() => {
            toast.dismiss();
            setFavoritesOpen(true);
          }}
          className="mt-4 text-xs text-white/50 hover:text-white/80 tracking-wide underline underline-offset-4 decoration-white/20 transition-colors duration-500"
          data-testid="open-favorites-btn"
          aria-label="See the lines you kept"
        >
          lines you kept · {favCount}
        </motion.button>
      )}
      </div>

      <FavoriteQuotes open={favoritesOpen} onClose={handleFavoritesClose} />
    </div>
  );
};

export default QuoteDisplay;
